import { useState } from 'react';
import { FaBan } from 'react-icons/fa';
import { Button, Modal } from '@/components/UI';

// Mirrors the backend cancel-reason limit so the input cannot submit a reason the API rejects.
const CANCEL_REASON_MAX_LENGTH = 2000;

interface CancelScorecardSessionButtonProps {
    sessionId: number;
    disabled: boolean;
    isCancelling: boolean;
    onCancel: (sessionId: number, reason: string, onCancelled: () => void) => void;
}

const CancelScorecardSessionButton = ({ sessionId, disabled, isCancelling, onCancel }: CancelScorecardSessionButtonProps) => {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const trimmedReason = reason.trim();

    const close = () => {
        setOpen(false);
        setReason('');
    };

    return (
        <>
            <Button disabled={disabled} icon={<FaBan />} size="sm" variant="danger" onClick={() => setOpen(true)}>
                Cancel Session
            </Button>
            <Modal
                draggable={false}
                footer={
                    <>
                        <Button disabled={isCancelling} variant="outline-dark" onClick={close}>
                            Keep Session
                        </Button>
                        <Button
                            disabled={trimmedReason.length === 0}
                            isLoading={isCancelling}
                            variant="danger"
                            onClick={() => onCancel(sessionId, trimmedReason, close)}
                        >
                            Cancel Session
                        </Button>
                    </>
                }
                isOpen={open}
                title={`Cancel AI scorecard session #${sessionId}`}
                onClose={isCancelling ? () => undefined : close}
            >
                <div className="space-y-3 text-sm text-zinc-300">
                    <p>
                        Cancelling stops this session permanently. Claimed features are released, any pending approval request is cancelled,
                        and agents can no longer add reviews, drafts, or approvals. Existing reviews and drafts stay visible.
                    </p>
                    <label className="flex flex-col gap-1">
                        <span className="text-xs text-zinc-400">Reason (required)</span>
                        <textarea
                            className="h-24 w-full resize-y rounded border border-zinc-700 bg-zinc-950 p-2 text-sm text-zinc-200"
                            maxLength={CANCEL_REASON_MAX_LENGTH}
                            placeholder="e.g. Superseded by a newer session on a corrected discovery run"
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                        />
                    </label>
                </div>
            </Modal>
        </>
    );
};

export default CancelScorecardSessionButton;
